import { Alert } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { api_auth } from '../../../../common/globals/constants';
import { AtCircularProgress } from '../../../../components/AtCircularProgress';
import AvailabilityDowntimeGlobal from '../../../../components/IndividualCharts/AvailabilityDowntimeGlobal/AvailabilityDowntimeGlobal';
import { useDynamicViewState } from '../../context/dynamic-view-context';
import { useFetchAvailabilityDowntimeGlobal } from '../../hooks/useFetchAvailabilityDowntimeGlobal';

/**
 * Genera los parámetros de URL para la petición de disponibilidad downtime global.
 *
 * @param {Object} params - Parámetros para generar la URL.
 * @param {number|string} params.objetivoId - ID del objetivo.
 * @param {Object} params.periodo - Configuración del período.
 * @param {string} params.periodo.period - Tipo de período ('d', 'w', 'm', 'c').
 * @param {Object} [params.periodo.statePeriod] - Estado del período personalizado.
 * @param {number|string} [params.pasoId=0] - ID del paso.
 * @param {number|string} [params.nodoId=0] - ID del nodo.
 * @param {number|string} [params.horarioId=0] - ID del horario.
 *
 * @returns {URLSearchParams} Parámetros de URL formateados.
 */
const URL_PARAMS = ({ objetivoId, periodo, pasoId = 0, horarioId = 0 }) => {
  let fechaInicio = '';
  let fechaTermino = '';

  if (periodo.period === 'c' && periodo.statePeriod) {
    fechaInicio = format(new Date(periodo.statePeriod.startDate), 'yyyy-MM-dd');
    fechaTermino = format(new Date(periodo.statePeriod.endDate), 'yyyy-MM-dd');
  }

  const token = sessionStorage.getItem('key');
  const user = sessionStorage.getItem('user');
  const URL = new URLSearchParams();
  URL.append('objetivoId', objetivoId);
  URL.append('token', token);
  URL.append('key', api_auth);
  URL.append('horarioId', horarioId);
  URL.append('fechaInicio', fechaInicio);
  URL.append('fechaTermino', fechaTermino);
  URL.append('nodoId', 0);
  URL.append('pasoId', pasoId);
  URL.append('user', user);
  URL.append('t', periodo.period);

  return URL;
};

/**
 * Componente Chart para mostrar downtime global por paso en paneles dinámicos.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {number|string} props.objetivoId - ID del objetivo a consultar.
 * @param {string} [props.name] - Nombre descriptivo del gráfico.
 * @param {number|string} [props.pasoId=0] - ID del paso específico.
 * @param {number|string} [props.nodoId=0] - ID del nodo específico.
 *
 * @returns {React.ReactElement} Componente de downtime global o estado de carga/error.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export const AvailabilityDowntimeGlobalChart = React.memo(
  ({ objetivoId, pasoId }) => {
    const { filterPeriod, refreshTime } = useDynamicViewState();

    const {
      data,
      isLoading,
      error,
      isFetching,
    } = useFetchAvailabilityDowntimeGlobal({
      urlParams: URL_PARAMS({
        objetivoId,
        periodo: filterPeriod,
        pasoId,
      }),
      fetchApi:
        filterPeriod.period === 'c'
          ? 'getDetalleDisponibilidadGlobalPasoDowntimeCustom'
          : 'getDetalleDisponibilidadGlobalPasoDowntime',
      refreshTime,
    });

    if (isLoading || isFetching) {
      return <AtCircularProgress />;
    }

    if (error) {
      return (
        <Alert severity="error">{error.message || 'Error loading data'}</Alert>
      );
    }

    return <AvailabilityDowntimeGlobal externalData={data} />;
  }
);
